import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';

// ICONS
import { ReactComponent as EyeOn } from '../../assets/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../assets/svg/eye-off.svg';

// TYPES
import { AuthenticationResetPasswordCommand } from '../../api/models';

interface ResetFormTypes {
	token?: string;
	email?: string;
	callback: () => void;
}

export const ResetForm: React.FC<ResetFormTypes> = ({ token, email, callback }) => {
	const [loading, setLoading] = React.useState<boolean>(false);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const formRef = React.createRef<HTMLFormElement>();
	const [showPassword, setShowPassword] = React.useState<boolean>(false);
	const [showConfirmPassword, setShowConfirmPassword] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (formRef.current) {
			formRef.current.addEventListener('click', setFormErrorMessage.bind(null, ''));
		}

		return () => {
			if (formRef.current) {
				formRef.current.removeEventListener('click', setFormErrorMessage.bind(null, ''));
			}
		};
	}, []);

	const initialValues: AuthenticationResetPasswordCommand = {
		token,
		email,
		password: '',
		confirmPassword: ''
	};

	const validationSchema = Yup.object({
		password: Yup.string()
			.required('Required')
			.min(12, 'Minimum of 12 characters')
			.matches(
				/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{12,}$/,
				'Please enter a valid password'
			),
		confirmPassword: Yup.string()
			.required('Required')
			.oneOf([Yup.ref('password'), null], 'Passwords must match')
	});

	const doReset = async (values: AuthenticationResetPasswordCommand) => {
		setLoading(true);

		const response = await apibridge.doResetPassword(values);

		setLoading(false);

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length ? validationErrorsToString(response.validationErrors) : response.message
			);
		} else if (response.result) {
			if (callback) {
				callback();
			}
		}
	};

	return (
		<Formik
			enableReinitialize={true}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={doReset}
		>
			{(formik) => {
				return (
					<Form translate="yes" ref={formRef}>
						<div className="content">
							<div className="form-group">
								<label htmlFor="password">New password</label>
								<div className="password-wrapper">
									<Field
										name="password"
										id="password"
										type={showPassword ? 'text' : 'password'}
										className="form-control"
									/>
									<Button
										className="btn-text"
										buttonIcon={showPassword ? <EyeOff /> : <EyeOn />}
										onClick={() => setShowPassword(!showPassword)}
									></Button>
								</div>
								<ErrorMessage name="password" component="div" className="form-validation-error" />
							</div>
							<div className="form-group">
								<label htmlFor="password">Confirm new password</label>

								<div className="password-wrapper">
									<Field
										name="confirmPassword"
										id="confirmPassword"
										type={showConfirmPassword ? 'text' : 'password'}
										className="form-control"
									/>
									<Button
										className="btn-text"
										buttonIcon={showConfirmPassword ? <EyeOff /> : <EyeOn />}
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									></Button>
								</div>

								<ErrorMessage name="confirmPassword" component="div" className="form-validation-error" />

								<div className="note">
									<ul className="traditional-list">
										<li>Minimum of 12 characters</li>
										<li>At least one lower case letter</li>
										<li>At least one upper case letter</li>
										<li>At least one number</li>
										<li>At least one special character (e.g. ! @ ?)</li>
									</ul>
								</div>
							</div>
						</div>
						<div className="actions">
							{!!formErrorMessage && <div className="form-validation-error">{formErrorMessage}</div>}
							<Button type="submit" className="btn-primary full-width" disabled={loading}>
								Reset password
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default ResetForm;
